import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth0 } from '../../hooks/useAuthPatched';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from '@mui/material';

const WARNING_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes
const REFRESH_POLL_MS = 60 * 1000; // re-check expiry every minute

const SessionWarning = () => {
    const { isAuthenticated, getIdTokenClaims, getAccessTokenSilently, logout } = useAuth0();
    const [showWarning, setShowWarning] = useState(false);
    const [timeRemaining, setTimeRemaining] = useState(null);
    const countdownIntervalRef = useRef(null);
    const warningTimerRef = useRef(null);
    const logoutTimerRef = useRef(null);
    // Hoisted above applyExpiry so the declaration order is unambiguous —
    // applyExpiry schedules a poll timer that reads this ref at fire time
    // (not at schedule time), so the value can be assigned after this point
    // in render order without risking a null read.
    const resetSessionTimersRef = useRef(null);
    // Monotonic version counter. Every async operation that intends to
    // eventually call applyExpiry() captures a version at its START and
    // only proceeds if the captured version is still the latest at the
    // time the result lands. Without this, a poll-timer's
    // getIdTokenClaims() that started before the user clicked Refresh
    // can resolve LAST with a stale ID-token expiry and overwrite the
    // fresh timers handleRefresh just installed.
    const requestVersionRef = useRef(0);
    // Tracks whether the component is still mounted. An async auth read
    // that resolves AFTER unmount can otherwise call applyExpiry() with
    // a version that is still "current" (no one has incremented it
    // since), schedule timers, and call setShowWarning / setTimeRemaining
    // on an unmounted component. The mounted guard short-circuits those
    // continuations before they touch React state or timer refs.
    const isMountedRef = useRef(true);
    // Authoritative expiry derived directly from a refreshed access-token's
    // expires_in (the detailedResponse path of handleRefresh). The Auth0
    // ID-token claims may not update in lockstep with the access-token
    // refresh, so a subsequent poll that reads getIdTokenClaims().exp can
    // see a STALE value that is earlier than the refresh we already
    // applied. Stash the authoritative expiry here so resetSessionTimers
    // can refuse to regress below it while it is still in the future.
    const authoritativeExpiryRef = useRef(null);

    const clearTimers = useCallback(() => {
        if (warningTimerRef.current) {
            clearTimeout(warningTimerRef.current);
            warningTimerRef.current = null;
        }
        if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
        }
        if (logoutTimerRef.current) {
            clearTimeout(logoutTimerRef.current);
            logoutTimerRef.current = null;
        }
    }, []);

    const scheduleLogout = useCallback((timeUntilExpiryMs) => {
        if (logoutTimerRef.current) {
            clearTimeout(logoutTimerRef.current);
            logoutTimerRef.current = null;
        }
        logoutTimerRef.current = setTimeout(() => {
            logout({ returnTo: window.location.origin });
        }, timeUntilExpiryMs);
    }, [logout]);

    const startCountdown = useCallback((timeUntilExpiryMs) => {
        if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
        }
        // Display the ceiling so the badge never shows "0" before the
        // exact-expiry logoutTimerRef has fired. Floor would round 999ms
        // down to 0 and create a confusing one-second window where the
        // banner reads "0:00" but the session is still alive.
        setTimeRemaining(Math.ceil(timeUntilExpiryMs / 1000));
        setShowWarning(true);

        // The interval ONLY updates the displayed countdown. The actual
        // logout is owned by logoutTimerRef (scheduled at exact-expiry
        // by scheduleLogout). Calling logout() from here too would
        // double-call it AND can fire up to ~1 second EARLIER than the
        // exact-expiry timer because the interval tick boundary doesn't
        // align with the absolute deadline.
        countdownIntervalRef.current = setInterval(() => {
            setTimeRemaining(prev => {
                const next = (prev ?? Math.ceil(timeUntilExpiryMs / 1000)) - 1;
                if (next <= 0) {
                    if (countdownIntervalRef.current) {
                        clearInterval(countdownIntervalRef.current);
                        countdownIntervalRef.current = null;
                    }
                    return 0;
                }
                return next;
            });
        }, 1000);
    }, []);

    // Apply an absolute expiry (ms since epoch) to the session timers.
    // Clears any previously-scheduled timers BEFORE scheduling new ones so
    // refresh cannot leak a stale countdown interval. Returns true if the
    // expiry produced a fresh timer schedule, false if it triggered logout.
    const applyExpiry = useCallback((expiryTimeMs) => {
        if (!expiryTimeMs) {
            return false;
        }
        const timeUntilExpiry = expiryTimeMs - Date.now();
        if (timeUntilExpiry <= 0) {
            clearTimers();
            logout({ returnTo: window.location.origin });
            return false;
        }

        clearTimers();
        scheduleLogout(timeUntilExpiry);

        if (timeUntilExpiry <= WARNING_THRESHOLD_MS) {
            startCountdown(timeUntilExpiry);
        } else {
            setShowWarning(false);
            setTimeRemaining(null);
        }

        // Wrap the callback so the timer reads resetSessionTimersRef.current
        // at FIRE TIME rather than capturing whatever value lives in the ref
        // at SCHEDULE TIME. The ref's value is populated by a useEffect
        // below, so on the very first applyExpiry call (e.g. mount-time)
        // the ref may still be null; reading at fire time defers the lookup
        // until after that effect has run and the latest resetSessionTimers
        // is in place.
        warningTimerRef.current = setTimeout(() => {
            const next = resetSessionTimersRef.current;
            if (typeof next === 'function') {
                next();
            }
        }, REFRESH_POLL_MS);
        return true;
    }, [clearTimers, logout, scheduleLogout, startCountdown]);

    // Version-guarded wrapper. Returns true if the caller's captured
    // version was still the latest, the component is still mounted,
    // and applyExpiry actually fired.
    const applyExpiryIfCurrent = useCallback((expiryTimeMs, capturedVersion) => {
        if (!isMountedRef.current) {
            // Component unmounted between the captured-version check and
            // the async result landing. Calling applyExpiry now would
            // schedule timers and dispatch setState on a dead component.
            return false;
        }
        if (requestVersionRef.current !== capturedVersion) {
            // A newer auth/expiry request has superseded this one.
            // Discard the stale result rather than overwriting the
            // timers the newer request just installed.
            return false;
        }
        return applyExpiry(expiryTimeMs);
    }, [applyExpiry]);

    // Latest resetSessionTimers stored on the hoisted ref (declared above
    // applyExpiry). Indirection avoids a useCallback dependency cycle
    // between applyExpiry and resetSessionTimers.
    const resetSessionTimers = useCallback(async () => {
        if (!isAuthenticated) {
            return;
        }
        // Capture the version BEFORE the async getIdTokenClaims so that
        // any newer request (e.g. handleRefresh kicked off by the user
        // while this poll is mid-flight) wins. The bump happens at the
        // start so concurrent older requests already in flight see
        // their captured version as < requestVersionRef.current.
        const myVersion = ++requestVersionRef.current;
        const claims = await getIdTokenClaims().catch(() => null);
        // Re-check the version IMMEDIATELY after the await — before
        // reading authoritativeExpiryRef or computing any state. If a
        // newer request has bumped the counter while we were awaiting
        // claims, we have no business reading shared expiry state or
        // calling applyExpiry: applyExpiryIfCurrent would reject anyway,
        // but bailing here keeps the read/decision path tight and avoids
        // surprising any future maintainer who adds side effects to the
        // expiry-resolution block.
        if (!isMountedRef.current || requestVersionRef.current !== myVersion) {
            return;
        }
        let expiryTime = claims?.exp ? claims.exp * 1000 : null; // ms
        // Authoritative-expiry floor: if a recent detailedResponse refresh
        // gave us a still-future expiry, never regress below it. The poll
        // can read a STALE ID-token claim that pre-dates the refresh; the
        // floor prevents the poll from quietly re-arming timers to the
        // pre-refresh expiry. Once the authoritative window has passed,
        // claims become the source of truth again.
        const authoritative = authoritativeExpiryRef.current;
        if (typeof authoritative === 'number' && authoritative > Date.now()) {
            expiryTime = typeof expiryTime === 'number'
                ? Math.max(expiryTime, authoritative)
                : authoritative;
        }
        if (!expiryTime) {
            return;
        }
        applyExpiryIfCurrent(expiryTime, myVersion);
    }, [applyExpiryIfCurrent, getIdTokenClaims, isAuthenticated]);

    useEffect(() => {
        resetSessionTimersRef.current = resetSessionTimers;
    }, [resetSessionTimers]);

    // Mount/unmount lifecycle for the mounted guard. Distinct from the
    // resetSessionTimers effect so the mounted-flag transitions are
    // owned by a single, dependency-free effect.
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            // Bump the version on unmount so any captured-but-unresolved
            // async will fail its version check even if no other request
            // has bumped the counter since.
            requestVersionRef.current += 1;
        };
    }, []);

    useEffect(() => {
        resetSessionTimers();
        return () => {
            clearTimers();
        };
    }, [clearTimers, resetSessionTimers]);

    const handleRefresh = async () => {
        try {
            // Prefer the SPA SDK's detailedResponse so we can read the
            // refreshed access-token's expires_in directly. The Auth0
            // access-token refresh may not propagate to the ID-token
            // claims immediately, so reading getIdTokenClaims().exp alone
            // can return a stale expiry and leave the countdown stuck.
            //
            // Capture a fresh version at the start so any older in-flight
            // resetSessionTimers (e.g. from the poll timer) can no longer
            // call applyExpiry after this handler resolves — its captured
            // version will be < requestVersionRef.current.
            const myVersion = ++requestVersionRef.current;
            let appliedFromDetailed = false;
            try {
                const detailed = await getAccessTokenSilently({
                    cacheMode: 'off',
                    detailedResponse: true,
                });
                if (detailed && typeof detailed.expires_in === 'number') {
                    const newExpiryMs = Date.now() + detailed.expires_in * 1000;
                    // Record the authoritative expiry BEFORE attempting to
                    // apply it. resetSessionTimers (the poll) uses this as
                    // a floor so it cannot regress to a stale ID-token
                    // claim while the access-token refresh is still in
                    // force. Stashing before applyExpiryIfCurrent matters
                    // for the unmounted/superseded paths too: even if
                    // applyExpiry bails, a concurrent poll should not
                    // adopt a pre-refresh expiry.
                    authoritativeExpiryRef.current = newExpiryMs;
                    appliedFromDetailed = applyExpiryIfCurrent(newExpiryMs, myVersion);
                }
            } catch (detailedErr) {
                // Detailed response is unsupported or refresh hit a
                // recoverable error; fall through to the claims path
                // below. A hard failure will surface from the next call
                // and trip the outer catch.
            }

            if (!appliedFromDetailed) {
                // Degraded fallback: forced refresh, then re-arm timers from
                // getIdTokenClaims().exp. Works on SDK versions without
                // detailedResponse support. If the forced refresh fails we
                // MUST let the error propagate to the outer catch (force
                // logout) — falling back to a cached getAccessTokenSilently()
                // would silently re-arm timers from stale ID-token claims
                // and pretend the session was renewed even though we have
                // no fresh token from the IdP.
                await getAccessTokenSilently({ cacheMode: 'off' });
                await resetSessionTimers();
            }
        } catch (e) {
            // If refresh fails, force logout (fail-safe).
            logout({ returnTo: window.location.origin });
        }
    };

    const formatTime = (seconds) => {
        const safe = seconds ?? 0;
        const minutes = Math.floor(safe / 60);
        const remainingSeconds = safe % 60;
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    };

    return (
        <Dialog open={showWarning} onClose={() => setShowWarning(false)}>
            <DialogTitle>Session Expiring Soon</DialogTitle>
            <DialogContent>
                <Typography>
                    Your session will expire in {formatTime(timeRemaining)}. Would you like to refresh your session?
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setShowWarning(false)} color="secondary">
                    Dismiss
                </Button>
                <Button onClick={handleRefresh} color="primary" variant="contained">
                    Refresh Session
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SessionWarning;
